import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  cashboxApi,
  type CashboxOperation,
  type CreateOperationDealInput,
  type CreateOperationInput,
  type CreateOperationsBatchInput,
  type OperationDirection,
} from '../../../lib/cashboxApi.js';
import { DealBlock, createEmptyDeal, dealGel, directionFromOpType, type DealDraft } from './DealBlock.js';

type Props = {
  operation?: CashboxOperation;
  onClose: () => void;
};

// Per design (mp-admin-preprod-2026-05-17/cashbox.jsx) the OperationModal has
// no top-level selectors — flow/direction/opType live inside each DealBlock.
// Direction is auto-derived from opType, flow is per-deal segment.
export function OperationModal({ operation, onClose }: Props) {
  const qc = useQueryClient();
  const isEdit = operation !== undefined;

  const [deals, setDeals] = useState<DealDraft[]>([
    operation
      ? {
          ...createEmptyDeal(),
          methods: [
            {
              type: operation.type,
              amountGel: operation.amountGel.toString(),
              currencyCode: 'GEL' as const,
              currencyAmount: '',
            },
          ],
          tracks: operation.tracks.join('\n'),
          trackList: [...operation.tracks],
          paymentMethod: operation.paymentMethod ?? '',
          account: operation.account ?? '',
          payerName: operation.payerName ?? '',
          comment: operation.comment ?? '',
          opType: operation.opType,
          collapsed: false,
        }
      : createEmptyDeal(),
  ]);
  const [operatedAt, setOperatedAt] = useState(
    operation?.operatedAt
      ? formatTbilisiDateTimeLocal(new Date(operation.operatedAt))
      : formatTbilisiDateTimeLocal(new Date()),
  );

  // Synchronous guard: the submit button is disabled on isPending, but a fast
  // double-click can fire onSubmit twice before the re-render disables it,
  // creating duplicate operations. The ref blocks the second call immediately.
  const submittingRef = useRef(false);

  const create = useMutation({
    mutationFn: (data: CreateOperationInput | CreateOperationsBatchInput) => cashboxApi.createOperation(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cashbox'] });
      onClose();
    },
    onError: () => {
      submittingRef.current = false;
    },
  });

  const update = useMutation({
    mutationFn: (data: { amountGel?: number; opType?: string; direction?: OperationDirection; comment?: string | null; tracks?: string[] }) =>
      cashboxApi.updateOperation(operation!.id, {
        ...data,
        expectedUpdatedAt: operation!.updatedAt,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cashbox'] });
      onClose();
    },
    onError: () => {
      submittingRef.current = false;
    },
  });

  const err = isEdit ? update.error : create.error;

  function onSubmit() {
    if (submittingRef.current) return; // ignore rapid double-clicks

    if (isEdit) {
      const deal = deals[0]!;
      const method = deal.methods[0]!;
      const amount = parseFloat(method.amountGel);
      if (!Number.isFinite(amount) || amount <= 0) {
        alert('Укажите корректную сумму');
        return;
      }
      const trackList =
        deal.trackList.length > 0
          ? deal.trackList
          : deal.tracks.split(/[\n,]+/).map((t) => t.trim()).filter(Boolean);
      submittingRef.current = true;
      // Фикс блокера ревью cashbox-optype-direction: шлём только реально
      // изменённое. direction из payload УДАЛЁН — его владелец сервер, и он
      // меняет направление только вместе со сменой opType; слать UI-фолбэк
      // ('expense' для легаси-типов) значило молча переворачивать
      // исторические income-строки правкой комментария. opType уходит только
      // при фактической смене (форма предлагает лишь словарные типы — сервер
      // выведет направление сам).
      update.mutate({
        amountGel: amount,
        ...(deal.opType !== operation!.opType ? { opType: deal.opType } : {}),
        comment: deal.comment || null,
        tracks: trackList,
      });
      return;
    }

    // Each payment method (with a positive amount) becomes its own operation;
    // tracks are optional. Empty method rows are skipped.
    const ops = deals.flatMap((deal) => parseDeal(deal, operatedAt));
    if (ops.length === 0) {
      alert('Укажите корректную сумму');
      return;
    }
    submittingRef.current = true;
    create.mutate(ops.length === 1 ? ops[0]! : { deals: ops });
  }

  function updateDeal(index: number, patch: Partial<DealDraft>) {
    setDeals((current) => current.map((deal, i) => (i === index ? { ...deal, ...patch } : deal)));
  }

  function addDeal() {
    // When a new deal is opened, collapse the previous ones to keep focus
    // on the new one (per design: only one expanded at a time during entry).
    setDeals((current) => [
      ...current.map((d) => ({ ...d, collapsed: true })),
      createEmptyDeal(),
    ]);
  }

  function removeDeal(index: number) {
    setDeals((current) => current.filter((_, i) => i !== index));
  }

  const isPending = create.isPending || update.isPending;

  // Esc closes the modal (unless a save is in flight).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, isPending]);

  const totalGel = deals.reduce((sum, d) => sum + dealGel(d), 0);
  // One operation per payment method with a positive amount.
  const opCount = deals.reduce(
    (n, d) => n + d.methods.filter((m) => (parseFloat(m.amountGel) || 0) > 0).length,
    0,
  );

  return (
    <div
      className="cb-overlay"
      data-testid="operation-modal"
      onClick={() => {
        if (!isPending) onClose();
      }}
    >
      <div className="cb-modal cb-op-modal" onClick={(e) => e.stopPropagation()}>
        <header className="cb-op-head" style={{ marginBottom: 12 }}>
          <p className="cb-modal-title" style={{ margin: 0 }}>
            {isEdit ? 'Редактирование операции' : 'Новая операция'}
          </p>
          <p className="muted" style={{ margin: '2px 0 0', fontSize: 12 }}>
            {isEdit
              ? 'Можно изменить сумму, тип, трек-номера и комментарий.'
              : 'Каждая сделка станет отдельной операцией в журнале кассы.'}
          </p>
        </header>

        <div className="cb-op-body">
          {deals.map((deal, index) => (
            <DealBlock
              storedDirection={operation?.direction}
              key={deal.id}
              deal={deal}
              index={index}
              canRemove={!isEdit && deals.length > 1}
              canEditMethods={!isEdit}
              onChange={(patch) => updateDeal(index, patch)}
              onRemove={() => removeDeal(index)}
            />
          ))}

          {!isEdit && (
            <button
              className="btn-admin ghost cb-add-deal"
              type="button"
              onClick={addDeal}
              data-testid="add-deal-btn"
              style={{ width: '100%', justifyContent: 'center', marginBottom: 10 }}
            >
              + Добавить сделку
            </button>
          )}

          {!isEdit && (
            <div
              className="cb-preview cb-multi-preview"
              data-testid="cb-preview"
              style={{
                padding: '12px 14px',
                background: 'var(--mnp-teal-50, #ecfdf5)',
                border: '1px solid var(--mnp-teal-200, #99f6e4)',
                borderRadius: 8,
                marginBottom: 10,
                fontSize: 13,
              }}
            >
              <p
                className="cb-preview-title"
                style={{
                  margin: '0 0 6px',
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.3,
                  color: 'var(--mnp-fg-2)',
                }}
              >
                Будет создано
              </p>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                <li>
                  <b>{opCount}</b> {pluralOps(opCount)} в журнале кассы
                </li>
                <li>
                  Общая сумма:{' '}
                  <b>{totalGel.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} GEL</b>
                </li>
                <li>Уведомления: Telegram-канал смены</li>
              </ul>
            </div>
          )}

          {!isEdit && (
            <div className="cb-field">
              <label>Дата и время операции</label>
              <input
                className="input-admin"
                type="datetime-local"
                value={operatedAt}
                onChange={(e) => setOperatedAt(e.target.value)}
              />
            </div>
          )}
        </div>

        {err && (
          <p style={{ color: 'var(--mnp-red-dark)', fontSize: 13, marginBottom: 8 }}>
            Ошибка: {err instanceof Error ? err.message : 'Неизвестная ошибка'}
          </p>
        )}

        <footer className="cb-op-foot cb-modal-actions">
          <button className="btn-admin" onClick={onClose} disabled={isPending}>
            Отмена
          </button>
          <button
            className="btn-admin primary"
            onClick={onSubmit}
            disabled={isPending}
            data-testid="op-submit-btn"
          >
            {isPending ? 'Сохраняю…' : isEdit ? 'Сохранить' : submitLabel(opCount)}
          </button>
        </footer>
      </div>
    </div>
  );
}

function parseDeal(deal: DealDraft, operatedAt: string): CreateOperationDealInput[] {
  // Prefer the chip list (canonical); fall back to legacy `tracks` text only
  // when the chip list is empty (handles drafts that never blurred the input).
  const trackList =
    deal.trackList.length > 0
      ? deal.trackList
      : deal.tracks.split(/[\n,]+/).map((t) => t.trim()).filter(Boolean);
  const direction = directionFromOpType(deal.opType);
  // One operation per payment method with a positive amount; empty rows skipped.
  return deal.methods
    .map((method) => ({
      method,
      amountGel: parseFloat(method.amountGel),
      currencyAmount: parseFloat(method.currencyAmount),
    }))
    .filter(({ amountGel }) => Number.isFinite(amountGel) && amountGel > 0)
    .map(({ method, amountGel, currencyAmount }) => ({
      type: method.type,
      direction,
      opType: deal.opType,
      operatedAt,
      amountGel,
      tracks: trackList,
      currencies:
        method.currencyCode === 'GEL' || !currencyAmount || currencyAmount <= 0
          ? []
          : [{ code: method.currencyCode, amount: currencyAmount }],
      paymentMethod: deal.paymentMethod || undefined,
      account: deal.account || undefined,
      payerName: deal.payerName || undefined,
      comment: deal.comment || undefined,
    }));
}

function pluralOps(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'операция';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'операции';
  return 'операций';
}

function submitLabel(n: number): string {
  if (n <= 1) return 'Создать';
  return `Создать ${n} ${pluralOps(n)}`;
}

function formatTbilisiDateTimeLocal(date: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tbilisi',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '00';
  return `${value('year')}-${value('month')}-${value('day')}T${value('hour')}:${value('minute')}`;
}
